import React, { useEffect, useState } from "react";
import {
  Users,        // total users
  UserCheck,    // active users
  UserX,        // blocked users
  Video,        // chakra videos
  Package,      // total products
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import axios from "axios";
import { format } from "date-fns";
import config from "../config";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUserCount: 0,
    activeUserCount: 0,
    blockedUserCount: 0,
    chakraVideoCount: 0,
    totalProductCount: 0,
    monthlySignups: [],
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${config.BASE_URL}/stats`);
        if (res.data.success) {
          setStats(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard stats", err);
      }
    };

    fetchStats();
  }, []);

  // Prepare chart data
  const signupMap = stats.monthlySignups.reduce((acc, entry) => {
    acc[entry.month] = parseInt(entry.count);
    return acc;
  }, {});

  const currentYear = new Date().getFullYear();
  const months = Array.from({ length: 12 }).map((_, i) => {
    const d = new Date(currentYear, i, 1);
    const monthKey = format(d, "yyyy-MM");
    return {
      name: format(d, "MMM"),
      value: signupMap[monthKey] || 0,
      color: "#3AC1CA",
    };
  });

  const totalNewUsers = months.reduce((sum, d) => sum + d.value, 0);

  // ✅ Stat cards in required order
  const statCards = [
    { label: "Total Users", value: stats.totalUserCount, icon: Users },
    { label: "Active Users", value: stats.activeUserCount, icon: UserCheck },
    { label: "Blocked Users", value: stats.blockedUserCount, icon: UserX },
    { label: "Chakra Videos", value: stats.chakraVideoCount, icon: Video },
    { label: "Total Products", value: stats.totalProductCount, icon: Package },
  ];

  return (
    <div className="min-h-screen bg-[#211436] p-8 space-y-8">
      {/* Stat Cards */}
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
        {statCards.map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            className="flex items-center gap-4 rounded-[10px] bg-[#351F57] p-5 shadow hover:shadow-lg transition-shadow duration-200"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-[#351F57]">
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-lg text-white">{label}</p>
              <p className="text-2xl font-semibold text-white">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Monthly User Signup Chart */}
      <div className="rounded-[10px] bg-[#351F57] p-6 shadow">
        <p className="mb-1 text-sm font-medium text-white">Total Sign Up</p>
        <div className="mb-4 text-3xl font-extrabold text-white">
          {totalNewUsers}
        </div>
        <div className="h-80 w-full">
          <ResponsiveContainer>
            <BarChart
              data={months}
              margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#3AC1CA" }}
              />
              <YAxis hide />
              <Tooltip cursor={{ fill: "transparent" }} />
              <ReferenceLine
                y={Math.max(...months.map((d) => d.value))}
                stroke="[#3AC1CA]"
                strokeDasharray="4 4"
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {months.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
